import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { IssuingEntity } from '@/types/domain'

/* An issuing entity's logo (FR-64, AD-18, migration 0041).
 *
 * The same three-step upload as the profile photo, and this file is deliberately
 * its close sibling — read `features/auth/api/useAvatar.ts` alongside it, because
 * every decision here was taken there first and the differences are the only part
 * worth reading twice:
 *
 *   1. POST /admin/entities/:id/logo/upload-url  → a ticket: URL, key, expiry, max
 *   2. PUT  <the ticket's URL>                   → bytes, direct to the bucket
 *   3. PUT  /admin/entities/:id/logo             → the key, once the bytes landed
 *
 * ── THE THREE DIFFERENCES FROM THE AVATAR ────────────────────────────────────
 *
 * THE SUBJECT IS NAMED. An avatar has no id anywhere in its file — a user may
 * only change their own, so the server takes the subject from the session and
 * there is nothing for the client to pass wrongly. An entity is not the caller, so
 * an id crosses the wire here, and what makes that safe is the server
 * re-verifying the returned key against THAT entity's prefix at commit
 * (storage.KeyBelongsTo). Without that check a Super-admin could point one legal
 * entity's letterhead at the other's object.
 *
 * THE LIST IS SHORTER. An avatar accepts four raster formats because it is
 * rendered by a browser; a logo accepts two, because it is drawn into a PDF by
 * gofpdf, which cannot sniff a format and no-ops the rest of a document when
 * handed the wrong one. WebP and GIF are refused for that reason and not for a
 * security one — see `platform/docs/logo.go`, which owns the list the server
 * enforces.
 *
 * THE CEILING IS LOWER. Two megabytes rather than five: a logo is artwork
 * exported at a chosen size, not a photograph straight off a phone.
 */

/** What the client may send. Enforced again server-side against the RENDERER's
 *  own list (`docs.LogoTypeDrawable`): this is the courtesy that stops a doomed
 *  upload, not the control. */
export const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg'] as const

/** The `accept` attribute for the file input, derived from the list above so the
 *  picker and the validation cannot disagree. */
export const LOGO_ACCEPT = ACCEPTED_LOGO_TYPES.join(',')

/** Mirrors settings.MaxLogoBytes. Checked here so a 20 MB export is refused
 *  before it is spent, and again at commit against the size storage reports —
 *  which is the number that actually decides. */
export const MAX_LOGO_BYTES = 2 * 1024 * 1024

/** The server's pre-signed upload instruction. Held only for one upload — the key
 *  confers nothing on its own, and the URL expires in minutes. */
interface LogoUploadTicket {
  objectKey: string
  url: string
  expiresAt: string
  maxBytes: number
  method: string
}

/** Why a logo was refused before it was ever sent. Codes rather than sentences:
 *  this client knows the user's language and the server deliberately does not
 *  compose prose for it (AD-27). */
export type LogoRejection = 'type' | 'size'

export class LogoRejectedError extends Error {
  // Declared and assigned rather than a `readonly reason` constructor parameter:
  // parameter properties emit code, which erasableSyntaxOnly forbids.
  readonly reason: LogoRejection

  constructor(reason: LogoRejection) {
    super(`The logo was refused (${reason}).`)
    this.name = 'LogoRejectedError'
    this.reason = reason
  }
}

/** Raised when the bucket refuses the bytes. Distinguished from an API error
 *  because the failure is between the browser and storage — our server never saw
 *  it, so there is no problem document to read. */
export class LogoUploadFailedError extends Error {
  readonly status: number

  constructor(status: number) {
    super(`The logo could not be sent to storage (${status}).`)
    this.name = 'LogoUploadFailedError'
    this.status = status
  }
}

/** Checks a file before any request is made.
 *
 *  Exported so the picker can reject without entering the mutation at all: an
 *  upload that cannot succeed should not produce a pending state first. */
export function rejectLogo(file: File): LogoRejection | null {
  // A whitelist rather than a `startsWith('image/')` test, for two independent
  // reasons. The renderer cannot draw anything else; and SVG is an image by every
  // reasonable reading of the word and a script by the browser's, rendered here
  // from the bucket's origin.
  if (!ACCEPTED_LOGO_TYPES.includes(file.type as (typeof ACCEPTED_LOGO_TYPES)[number])) {
    return 'type'
  }
  if (file.size > MAX_LOGO_BYTES) return 'size'
  return null
}

/** Uploads an entity's logo through the three-step (FR-64, AD-18).
 *
 *  NOT optimistic, on the avatar's argument: a local object URL shown before the
 *  bytes land is a mark only this browser can see, on a screen whose whole job is
 *  to show what the company's documents will actually carry — and it would survive
 *  a failed upload looking exactly like a success. Pending is shown as pending.
 */
export function useUploadEntityLogo() {
  const queryClient = useQueryClient()

  return useMutation<IssuingEntity, Error, { id: string; file: File }>({
    mutationFn: async ({ id, file }) => {
      const rejection = rejectLogo(file)
      if (rejection) throw new LogoRejectedError(rejection)

      if (USE_MOCKS) {
        // A local preview is honest offline: there is no bucket, so this IS the
        // logo for as long as the tab lives. The byte size is the real one; the
        // content type is what the file declared.
        const entities = queryClient.getQueryData<IssuingEntity[]>(['settings', 'entities'])
        const entity = entities?.find((e) => e.id === id)
        if (!entity) throw new Error(`no entity ${id} in the mock cache`)
        return {
          ...entity,
          logo: {
            has: true,
            url: URL.createObjectURL(file),
            contentType: file.type,
            byteSize: file.size,
          },
        }
      }

      // (1) Ask for somewhere to put it. The server names the key; we never do.
      const ticket = await api.post<LogoUploadTicket>(
        `/admin/entities/${id}/logo/upload-url`,
        { fileName: file.name, contentType: file.type },
      )

      // (2) Straight to the bucket.
      //
      // Bare fetch rather than the api client: this request does not go to our
      // API, must not carry our session cookie to a third-party host, and its
      // failure is an HTTP status from storage rather than a problem document.
      const upload = await fetch(ticket.url, {
        method: ticket.method,
        body: file,
        headers: file.type ? { 'Content-Type': file.type } : undefined,
      })
      if (!upload.ok) throw new LogoUploadFailedError(upload.status)

      // (3) Record it. The server re-verifies the key against THIS entity's own
      // prefix, confirms with storage that the object landed, and answers the
      // updated entity — so the mark appears without a second request.
      return api.put<IssuingEntity>(`/admin/entities/${id}/logo`, {
        objectKey: ticket.objectKey,
        contentType: file.type,
      })
    },

    onSuccess: (entity) => {
      // Seed rather than invalidate: the commit already answered the whole
      // entity, and refetching would spend a round trip to be told the same
      // thing — plus a second signing of the URL we already hold.
      queryClient.setQueryData<IssuingEntity[]>(['settings', 'entities'], (list) =>
        list?.map((e) => (e.id === entity.id ? entity : e)),
      )
    },
  })
}

/** Removes an entity's logo, returning its documents to the wordmark. */
export function useRemoveEntityLogo() {
  const queryClient = useQueryClient()

  return useMutation<IssuingEntity, Error, string>({
    mutationFn: async (id) => {
      if (USE_MOCKS) {
        const entities = queryClient.getQueryData<IssuingEntity[]>(['settings', 'entities'])
        const entity = entities?.find((e) => e.id === id)
        if (!entity) throw new Error(`no entity ${id} in the mock cache`)
        return { ...entity, logo: { has: false } }
      }
      return api.delete<IssuingEntity>(`/admin/entities/${id}/logo`)
    },

    onSuccess: (entity) => {
      queryClient.setQueryData<IssuingEntity[]>(['settings', 'entities'], (list) =>
        list?.map((e) => (e.id === entity.id ? entity : e)),
      )
    },
  })
}
